import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import Layout from "../components/layout/GSTlayout";
import config from "../config.js";

import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const DeadlineTrackerPage = () => {
  const [cookies] = useCookies(["userId"]);
  const { API_BASE_URL } = config;
  
  const [deadlines, setDeadlines] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      category: "",
      title: "",
      dueDate: "",
      description: "",
      documents: "",
    },
  });

  const fetchDeadlines = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/deadlines/${cookies.userId}`);
      setDeadlines(response.data);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch deadlines");
    } finally {
      setLoading(false);
    }
  };

  const createDeadline = async (data) => {
    try {
      setLoading(true);
      await axios.post(
        `${API_BASE_URL}/api/v1/deadlines`,
        {
          userId: cookies.userId, 
          ...data,
          documents: data.documents.split(",").map((doc) => doc.trim()), 
        },
        {
          headers: {
           
          },
        }
      );
      await fetchDeadlines();
      setOpenDialog(false);
      reset();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to create deadline");
    } finally {
      setLoading(false);
    }
  };

  const updateDeadline = async (id, updateData) => {
    try {
      setLoading(true);
      await axios.put(
        `${API_BASE_URL}/api/v1/deadlines/${id}`,
        updateData,
        {
          headers: {
            userId: cookies.userId,
          },
        }
      );
      await fetchDeadlines();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update deadline");
    } finally {
      setLoading(false);
    }
  };

  const deleteDeadline = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/api/v1/deadlines/${id}`, {
        headers: {
          userId: cookies.userId,
        },
      });
      await fetchDeadlines();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete deadline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeadlines();
  }, []);

  const calculateDeadlineStatus = (deadline) => {
    const today = new Date();
    const dueDate = new Date(deadline.dueDate);

    if (deadline.completedDate) return "completed";
    if (dueDate < today) return "overdue";
    return "pending";
  };

  const handleMarkCompleted = async (id) => {
    await updateDeadline(id, {
      completedDate: new Date().toISOString(),
      status: "completed",
    });
  };

  const renderStatusChip = (deadline) => {
    const status = calculateDeadlineStatus(deadline);
    switch (status) {
      case "overdue":
        return (
          <Chip
            icon={<WarningAmberIcon />}
            label="Overdue"
            color="error"
            size="small"
          />
        );
      case "pending":
        return (
          <Chip
            icon={<CalendarTodayIcon />}
            label="Pending"
            color="warning"
            size="small"
          />
        );
      case "completed":
        return (
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label="Completed"
            color="success"
            size="small"
          />
        );
    }
  };

  const overallCompliancePercentage = () => {
    const totalDeadlines = deadlines.length;
    const completedDeadlines = deadlines.filter(
      (deadline) => calculateDeadlineStatus(deadline) === "completed"
    ).length;
    return totalDeadlines ? Math.round((completedDeadlines / totalDeadlines) * 100) : 0;
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ width: "100%", mt: 2 }}>
          <LinearProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Card sx={{ maxWidth: 1200, margin: "auto" }}>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              <Typography variant="h4" gutterBottom>
                GST Deadline Tracker
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenDialog(true)}
              >
                Add New Deadline
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%", mt: 2, mb: 2 }}>
            <Typography variant="h6">
              Overall Compliance: {overallCompliancePercentage()}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={overallCompliancePercentage()}
              color={
                overallCompliancePercentage() < 50
                  ? "error"
                  : overallCompliancePercentage() < 75
                  ? "warning"
                  : "success"
              }
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Deadline Timeline
          </Typography>
          <Stepper orientation="vertical">
            {deadlines.map((deadline) => {
              const status = calculateDeadlineStatus(deadline);
              return (
                <Step key={deadline._id} active={true}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor:
                            status === "completed"
                              ? "success.main"
                              : status === "overdue"
                              ? "error.main"
                              : "warning.main",
                        }}
                      />
                    )}
                  >
                    {deadline.title}
                  </StepLabel>
                  <StepContent>
                    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                      <Typography variant="body2">
                        {deadline.category} | Due: {new Date(deadline.dueDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {deadline.description}
                      </Typography>
                      {renderStatusChip(deadline)}
                      {status !== "completed" && (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleMarkCompleted(deadline._id)}
                          sx={{ ml: 1, mt: 1 }}
                        >
                          Mark Completed
                        </Button>
                      )}
                    </Paper>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Detailed Deadlines
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Required Documents</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deadlines.map((deadline) => (
                  <TableRow key={deadline._id}>
                    <TableCell>{deadline.category}</TableCell>
                    <TableCell>{deadline.title}</TableCell>
                    <TableCell>
                      {new Date(deadline.dueDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{renderStatusChip(deadline)}</TableCell>
                    <TableCell>
                      {deadline.documents.map((doc, index) => (
                        <Chip
                          key={index}
                          label={doc}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {calculateDeadlineStatus(deadline) !== "completed" && (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => handleMarkCompleted(deadline._id)}
                            sx={{ mr: 1 }}
                          >
                            Complete
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => deleteDeadline(deadline._id)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={openDialog}
            onClose={() => {
              setOpenDialog(false);
              reset();
            }}
            maxWidth="md"
            fullWidth
          >
            <form onSubmit={handleSubmit(createDeadline)}>
              <DialogTitle>Add New Deadline</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="category"
                      control={control}
                      rules={{ required: "Category is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Category"
                          error={!!errors.category}
                          helperText={errors.category?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="title"
                      control={control}
                      rules={{ required: "Title is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Title"
                          error={!!errors.title}
                          helperText={errors.title?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="dueDate"
                      control={control}
                      rules={{ required: "Due date is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Due Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.dueDate}
                          helperText={errors.dueDate?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="documents"
                      control={control}
                      rules={{ required: "Required documents are required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Required Documents (comma-separated)"
                          error={!!errors.documents}
                          helperText={errors.documents?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="description"
                      control={control}
                      rules={{ required: "Description is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          label="Description"
                          error={!!errors.description}
                          helperText={errors.description?.message}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setOpenDialog(false);
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  Add Deadline
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default DeadlineTrackerPage;
